import React, { useState } from "react";
import { CircularProgress, Checkbox, Snackbar, Alert, Autocomplete, TextField } from "@mui/material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ISO6391 from "iso-639-1";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import TranslateIcon from "@mui/icons-material/Translate";
import PublicIcon from "@mui/icons-material/Public";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LanguageIcon from "@mui/icons-material/Language";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";

import { CustomInput, CustomTextArea } from "../../../components/Common/CustomInputs";
import AvatarEditorModal from "./AvatarEditorModal";
import { CustomBtn } from "../../../components/Common/CustomBtn";
import { CustomDropdown } from "../../../components/Common/CustomDropdown";
import CustomTagInput from "../../../components/Common/CustomTagInput";
import FileUploadButton from "../../../utils/FileUploadButton";
import UploadedFileNote from "../../../utils/UploadedFileNote";
import { getInitials } from "../../../components/ui/Gapper";
import { colors } from "../../../theme/theme";

import { useMyProfile } from "./MyProfile.hooks";
import {
  PAGE_TITLE,
  PAGE_SUBTITLE,
  GENDER_OPTIONS,
  COMPLIANCE_DOCS,
} from "./MyProfile.constants";
import {
  RootBox,
  HeaderRow,
  PageHeading,
  PageSubheading,
  LoadingBox,
  SectionCard,
  SectionTitle,
  FieldGrid,
  FullRow,
  AvatarRow,
  ProfileAvatar,
  AvatarName,
  AvatarSubtitle,
  CheckboxRow,
  StatusPill,
  FieldLabel,
  InputFieldLabel,
  phoneInputStyle,
  phoneButtonStyle,
  SaveBar,
  AvatarWrap,
  AvatarEditButton,
  DocRow,
  DocHeader,
  DocControls,
  HelperText,
  ReviewBanner,
  ReviewBannerTitle,
  ReviewBannerText,
} from "./MyProfile.styles";

const MyProfile = () => {
  const {
    loading,
    saving,
    uploading,
    form,
    pendingEdit,
    setField,
    handleSingleUpload,
    handleMultiUpload,
    handleRemoveFile,
    handleSubmit,
    snack,
    closeSnack,
  } = useMyProfile();

  const [avatarModalOpen, setAvatarModalOpen] = useState(false);

  const LANGUAGE_OPTIONS = ISO6391.getAllNames();

  if (loading) {
    return (
      <LoadingBox>
        <CircularProgress sx={{ color: colors.secondary }} />
      </LoadingBox>
    );
  }

  const fullName = `${form.firstName} ${form.lastName}`.trim();
  const subtitle = (form.specialities || []).slice(0, 2).join(" · ");

  const SaveButton = (
    <CustomBtn
      text={saving ? "Saving..." : "Save Changes"}
      onClick={handleSubmit}
      disabled={saving}
      noMb
      noMt
    />
  );

  return (
    <RootBox>
      <HeaderRow>
        <div>
          <PageHeading>{PAGE_TITLE}</PageHeading>
          <PageSubheading>{PAGE_SUBTITLE}</PageSubheading>
        </div>
        {SaveButton}
      </HeaderRow>

      {pendingEdit && pendingEdit.status === "pending" && (
        <ReviewBanner>
          <HourglassEmptyIcon fontSize="small" />
          <div>
            <ReviewBannerTitle>Changes awaiting review</ReviewBannerTitle>
            <ReviewBannerText>
              Your latest changes have been submitted and will appear on the Nirva
              site once an admin publishes them. You can keep editing and resubmit.
            </ReviewBannerText>
          </div>
        </ReviewBanner>
      )}
      {pendingEdit && pendingEdit.status === "rejected" && (
        <ReviewBanner $rejected>
          <ErrorOutlineIcon fontSize="small" />
          <div>
            <ReviewBannerTitle>Changes need attention</ReviewBannerTitle>
            <ReviewBannerText>
              An admin reviewed your changes and did not publish them.
              {pendingEdit.reviewComment ? ` Note: ${pendingEdit.reviewComment}` : ""}
              {" "}Please update your profile and submit again.
            </ReviewBannerText>
          </div>
        </ReviewBanner>
      )}

      {/* Identity summary + avatar (click to view / reposition / replace) */}
      <SectionCard>
        <AvatarRow>
          <AvatarWrap>
            <ProfileAvatar
              src={form.avatar || undefined}
              onClick={() => setAvatarModalOpen(true)}
              sx={{ cursor: "pointer" }}
              imgProps={{ style: { objectPosition: form.avatarPosition || "center" } }}
            >
              {uploading.avatar ? (
                <CircularProgress size={20} sx={{ color: colors.white }} />
              ) : (
                getInitials(fullName)
              )}
            </ProfileAvatar>
            <AvatarEditButton
              type="button"
              onClick={() => setAvatarModalOpen(true)}
              aria-label="Edit avatar"
            >
              <PhotoCameraIcon />
            </AvatarEditButton>
          </AvatarWrap>
          <div>
            <AvatarName>{fullName || "Your name"}</AvatarName>
            {subtitle && <AvatarSubtitle>{subtitle}</AvatarSubtitle>}
          </div>
        </AvatarRow>
      </SectionCard>

      <AvatarEditorModal
        open={avatarModalOpen}
        onClose={() => setAvatarModalOpen(false)}
        avatar={form.avatar}
        position={form.avatarPosition}
        uploading={!!uploading.avatar}
        onReplace={(file) => handleSingleUpload("avatar", file)}
        onPositionChange={(pos) => setField("avatarPosition", pos)}
      />

      {/* Personal information */}
      <SectionCard>
        <SectionTitle>
          <PersonOutlineIcon fontSize="small" /> Personal Information
        </SectionTitle>
        <FieldGrid>
          <CustomInput
            label="First Name"
            required
            value={form.firstName}
            setValue={(v) => setField("firstName", v)}
            noIcon
          />
          <CustomInput
            label="Last Name"
            required
            value={form.lastName}
            setValue={(v) => setField("lastName", v)}
            noIcon
          />
          {/* Email is read-only — changing it needs a verified flow. */}
          <CustomInput label="Email" value={form.email} setValue={() => {}} noIcon />
          <div>
            <InputFieldLabel>Phone</InputFieldLabel>
            <PhoneInput
              country="us"
              value={(form.phone || "").replace(/^\+/, "")}
              onChange={(v) => setField("phone", v ? `+${v}` : "")}
              enableSearch
              countryCodeEditable
              specialLabel=""
              containerStyle={{ width: "100%" }}
              inputStyle={phoneInputStyle}
              buttonStyle={phoneButtonStyle}
              dropdownStyle={{ fontFamily: "'Quicksand', sans-serif" }}
            />
          </div>
          <CustomDropdown
            label="Gender"
            value={form.gender}
            setValue={(v) => setField("gender", v)}
            data={GENDER_OPTIONS}
            placeholder="Select"
            noIcon
          />
          <CustomInput
            label="Time Zone"
            value={form.timeZone}
            setValue={(v) => setField("timeZone", v)}
            noIcon
          />
          <FullRow>
            <CustomTextArea
              label="Bio"
              value={form.bio}
              setValue={(v) => setField("bio", v)}
              rows={10}
              noIcon
            />
          </FullRow>
        </FieldGrid>
      </SectionCard>

      {/* Professional details */}
      <SectionCard>
        <SectionTitle>
          <WorkOutlineIcon fontSize="small" /> Professional Details
        </SectionTitle>
        <FieldGrid>
          <FullRow>
            <CustomTagInput
              label="Specialities"
              hashTags={form.specialities}
              setHashTags={(v) => setField("specialities", v)}
              placeholder="Add a speciality and press Enter"
            />
          </FullRow>
          <CustomInput
            label="Hourly Rate (USD)"
            type="number"
            value={form.hourlyRate}
            setValue={(v) => setField("hourlyRate", v)}
            noIcon
          />
          <div>
            <FieldLabel>Practitioner Status</FieldLabel>
            <StatusPill $approved={form.practitionerStatus}>
              {form.practitionerStatus ? "Approved" : "Pending Review"}
            </StatusPill>
          </div>
          <FullRow>
            <CheckboxRow>
              <Checkbox
                checked={!!form.licensed}
                onChange={(e) => setField("licensed", e.target.checked)}
                sx={{ color: colors.secondary, "&.Mui-checked": { color: colors.secondary } }}
              />
              I hold a valid professional license
            </CheckboxRow>
          </FullRow>
          <FullRow>
            <CustomTextArea
              label="Experience (one entry per line)"
              value={form.experience}
              setValue={(v) => setField("experience", v)}
              rows={4}
              noIcon
            />
          </FullRow>
          <FullRow>
            <CustomTextArea
              label="Education (one entry per line)"
              value={form.education}
              setValue={(v) => setField("education", v)}
              rows={4}
              noIcon
            />
          </FullRow>
          <FullRow>
            <CustomTextArea
              label="Certifications (one entry per line)"
              value={form.certifications}
              setValue={(v) => setField("certifications", v)}
              rows={4}
              noIcon
            />
          </FullRow>
        </FieldGrid>
      </SectionCard>

      {/* Languages — searchable multi-select from the ISO 639 list */}
      <SectionCard>
        <SectionTitle>
          <TranslateIcon fontSize="small" /> Languages
        </SectionTitle>
        <FieldLabel>Languages Spoken</FieldLabel>
        <Autocomplete
          multiple
          options={LANGUAGE_OPTIONS}
          value={Array.isArray(form.languages) ? form.languages : []}
          onChange={(e, v) => setField("languages", v)}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField {...params} placeholder="Search and select languages" size="small" />
          )}
        />
      </SectionCard>

      {/* Social & web */}
      <SectionCard>
        <SectionTitle>
          <PublicIcon fontSize="small" /> Social & Web Presence
        </SectionTitle>
        <FieldGrid>
          <CustomInput
            label="Instagram"
            value={form.instagram}
            setValue={(v) => setField("instagram", v)}
            placeholder="https://instagram.com/yourprofile"
            Icon={InstagramIcon}
            iconColor="#E4405F"
          />
          <CustomInput
            label="Facebook"
            value={form.fb}
            setValue={(v) => setField("fb", v)}
            placeholder="https://facebook.com/yourprofile"
            Icon={FacebookIcon}
            iconColor="#1877F2"
          />
          <CustomInput
            label="LinkedIn"
            value={form.linkedIn}
            setValue={(v) => setField("linkedIn", v)}
            placeholder="https://linkedin.com/in/yourprofile"
            Icon={LinkedInIcon}
            iconColor="#0A66C2"
          />
          <CustomInput
            label="Website"
            value={form.website}
            setValue={(v) => setField("website", v)}
            placeholder="https://yoursite.com"
            Icon={LanguageIcon}
            iconColor={colors.secondary}
          />
          <FullRow>
            <CustomInput
              label="SimplePractice Booking Link"
              value={form.simplePracticeBookingUrl}
              setValue={(v) => setField("simplePracticeBookingUrl", v)}
              placeholder="https://..."
              Icon={EventAvailableIcon}
              iconColor={colors.secondary}
            />
          </FullRow>
        </FieldGrid>
      </SectionCard>

      {/* Compliance documents — upload / view / delete */}
      <SectionCard>
        <SectionTitle>
          <DescriptionOutlinedIcon fontSize="small" /> Compliance Documents
        </SectionTitle>
        <HelperText sx={{ mb: 2 }}>
          Upload PDF or image files. Documents are reviewed by the Nirva team.
        </HelperText>
        {COMPLIANCE_DOCS.map((doc) => {
          const value = form[doc.key];
          const uploaded = doc.multi ? (value || []).length > 0 : !!value;
          const busy = !!uploading[doc.key];
          const inputId = `doc-${doc.key}`;
          return (
            <DocRow key={doc.key}>
              <DocHeader>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontFamily: "inherit",
                  }}
                >
                  {uploaded ? (
                    <CheckCircleIcon fontSize="small" sx={{ color: colors.success }} />
                  ) : (
                    <RadioButtonUncheckedIcon fontSize="small" sx={{ color: colors.lightBorder }} />
                  )}
                  {doc.label}
                  {doc.multi && <HelperText>(multiple allowed)</HelperText>}
                </span>
                <StatusPill $approved={uploaded}>
                  {uploaded ? "Complete" : "Pending"}
                </StatusPill>
              </DocHeader>
              <DocControls>
                <FileUploadButton
                  id={inputId}
                  label={busy ? "Uploading..." : uploaded && !doc.multi ? "Replace" : "Upload"}
                  accept={doc.accept}
                  multiple={doc.multi}
                  onChange={(e) => {
                    if (doc.multi) handleMultiUpload(doc.key, e.target.files);
                    else handleSingleUpload(doc.key, e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                {busy && <CircularProgress size={18} sx={{ color: colors.secondary }} />}
              </DocControls>
              <UploadedFileNote
                file={value}
                onDelete={(f, idx) => handleRemoveFile(doc.key, doc.multi ? idx : undefined)}
              />
            </DocRow>
          );
        })}
      </SectionCard>

      <SaveBar>{SaveButton}</SaveBar>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={closeSnack} severity={snack.severity} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </RootBox>
  );
};

export default MyProfile;
